import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

const footerImg = "https://images.pexels.com/photos/34155535/pexels-photo-34155535.jpeg";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're on the list. Welcome to the tide.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="relative bg-primary text-primary-foreground overflow-hidden" data-testid="footer">
      <div
        className="absolute inset-0 opacity-30 bg-cover bg-center"
        style={{ backgroundImage: `url(${footerImg})` }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-primary/70" aria-hidden />

      <div className="relative mx-auto max-w-[1600px] px-6 md:px-12 py-24 md:py-32">
        <div className="grid md:grid-cols-2 gap-16 md:gap-24">
          <div>
            <p className="label text-primary-foreground/60 mb-6">/ Newsletter</p>
            <h2 className="font-display text-4xl md:text-6xl font-light leading-[0.95] tracking-tight max-w-xl">
              Dispatches from the shoreline.
            </h2>
            <p className="mt-6 text-sm text-primary-foreground/70 max-w-md">
              Formulation notes, restock alerts, and the occasional field study. No noise.
            </p>
            <form onSubmit={subscribe} className="mt-10 flex border-b border-primary-foreground/40 max-w-md" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="flex-1 bg-transparent py-4 text-sm placeholder:text-primary-foreground/40 focus:outline-none"
                data-testid="newsletter-email-input"
              />
              <button
                type="submit"
                disabled={busy}
                className="label flex items-center gap-2 hover:opacity-70 transition-opacity disabled:opacity-40"
                data-testid="newsletter-submit"
              >
                Subscribe <ArrowUpRight size={16} strokeWidth={1.5} />
              </button>
            </form>
          </div>

          <div className="grid grid-cols-2 gap-10 md:justify-items-end">
            <div>
              <p className="label text-primary-foreground/60 mb-6">Explore</p>
              <ul className="space-y-3 text-sm">
                <li><Link to="/shop" className="hover:text-accent transition-colors" data-testid="footer-shop">All Products</Link></li>
                <li><Link to="/contact" className="hover:text-accent transition-colors" data-testid="footer-contact">Contact</Link></li>
                <li><Link to="/affiliate" className="hover:text-accent transition-colors" data-testid="footer-affiliate">Become an Affiliate</Link></li>
                <li><Link to="/terms" className="hover:text-accent transition-colors" data-testid="footer-terms">Terms &amp; Conditions</Link></li>
                <li><Link to="/account" className="hover:text-accent transition-colors" data-testid="footer-account">Account</Link></li>
              </ul>
            </div>
            <div>
              <p className="label text-primary-foreground/60 mb-6">Contact</p>
              <ul className="space-y-3 text-sm text-primary-foreground/80">
                <li>coastchems@gmail.com</li>
                <li>Pacific Coast Lab</li>
                <li>Mon–Fri · 9–5 PT</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-primary-foreground/20">
          <p className="label text-accent text-center tracking-[0.25em]" data-testid="footer-disclaimer">
            Research Use Only · Not For Human Consumption
          </p>
        </div>

        <div className="mt-8 pt-8 border-t border-primary-foreground/20 flex flex-col md:flex-row justify-between gap-4 text-xs text-primary-foreground/50">
          <span className="font-display text-lg text-primary-foreground/80">Coastal Chemistry</span>
          <span>© {new Date().getFullYear()} · Formulated with precision · All rights reserved</span>
        </div>
      </div>
    </footer>
  );
}
